import ReactDOM from 'react-dom';

import TrackingActions, {
	TRACKING_ACTIONS,
	TRACKING_CATEGORIES,
	TRACKING_EVENTS,
} from '~/services/actions/tracking-actions';
import US_STATE_CODE from '~/enums/us-state-code';
import type IArticleAuthor from '../../../../types/article-author';

export function FormatString(format: string, ...args: Array<string>): string {
	return format.replace(/{(\d+)}/g, function (match, number) {
		return typeof args[number] !== 'undefined' ? args[number] : match;
	});
}

/**
 * Comp a collection of styles instead of doing styles.foo + " " + styles.bar
 *
 * @export
 * @param {Array<string>} list of style classes
 */
export function comp(...arrayOfStrings): string {
	return arrayOfStrings.join(' ');
}

/**
 * Scroll to a given element, with optional topOffset
 *
 * @export
 * @param {any} target Element you want to scroll to
 * @param {number} topOffset Top offset to ensure element not hidden by menu
 */
export function scrollTo(target: any, topOffset = 0) {
	const targetScrollDepth = target?.getBoundingClientRect()?.top;
	const visibleHeader = document.getElementById(
		`${targetScrollDepth <= 0 ? 'headerWrapper' : 'headerSubMenu'}`,
	);

	let visibleHeaderHeight = 0;
	if (visibleHeader) {
		visibleHeaderHeight = visibleHeader?.getBoundingClientRect()?.height;
	}

	const windowScrollDepth =
		window.scrollY || (document.documentElement || document.body).scrollTop;

	window.scrollTo({
		left: 0,
		top:
			targetScrollDepth + windowScrollDepth - visibleHeaderHeight + topOffset,
		behavior: 'smooth',
	});
}

export function pageScroll() {
	// return document.body.scrollTop || document.documentElement.scrollTop;
	return window.scrollY || document.documentElement.scrollTop;
}

export function getInternetExplorerVersion(): any {
	if (typeof window === 'undefined') {
		return -1;
	}

	let rv = -1;
	if (navigator.appName == 'Microsoft Internet Explorer') {
		var ua = navigator.userAgent; // eslint-disable-line no-var
		var re = new RegExp('MSIE ([0-9]{1,}[.0-9]{0,})'); // eslint-disable-line no-var
		if (re.exec(ua) != null) {
			rv = parseFloat(RegExp.$1);
		}
	} else if (navigator.appName == 'Netscape') {
		var ua = navigator.userAgent; // eslint-disable-line no-var
		var re = new RegExp('Trident/.*rv:([0-9]{1,}[.0-9]{0,})'); // eslint-disable-line no-var
		if (re.exec(ua) != null) {
			rv = parseFloat(RegExp.$1);
		}
	}
	return rv;
}

export function isMountedComponent(component) {
	// exceptions for flow control :(
	try {
		ReactDOM.findDOMNode(component); // eslint-disable-line react/no-find-dom-node
		return true;
	} catch (e) {
		// Error: Invariant Violation: Component (with keys: props,context,state,refs,_reactInternalInstance) contains `render` method but is not mounted in the DOM
		return false;
	}
}

export const getPointSpreadValueFromName = (
	name: string,
): string | number | undefined => {
	if (typeof name !== 'string') {
		return '';
	}

	let value = name;

	// 1. handle no numbers found, by returning an empty string
	const numbers = value.match(/\d+/g);
	if (!numbers) {
		return '';
	}

	// 2. remove undesired chars from our string, i.e. anything
	// that is not a digit(\d), plus(+), minus(-), or dot(.)
	const invalidCharsRegex = new RegExp(/[^\d+-.]/, 'g');
	if (value.match(invalidCharsRegex)) {
		value = value.replace(invalidCharsRegex, '');
	}

	// 3. locate desired substring index
	const lastIndexOfPlusChar = value.lastIndexOf('+');
	const lastIndexOfMinusChar = value.lastIndexOf('-');

	const hasPlusChar = lastIndexOfPlusChar !== -1;
	const hasMinusChar = lastIndexOfMinusChar !== -1;

	switch (true) {
		case hasPlusChar && hasMinusChar: {
			const [_, lastIndexOfPlusOrMinusChar] = [
				lastIndexOfPlusChar,
				lastIndexOfMinusChar,
			].sort();

			return value.substring(lastIndexOfPlusOrMinusChar);
		}
		case hasPlusChar && !hasMinusChar: {
			return value.substring(lastIndexOfPlusChar);
		}
		case !hasPlusChar && hasMinusChar: {
			return value.substring(lastIndexOfMinusChar);
		}
		case !hasPlusChar && !hasMinusChar: {
			return value;
		}
	}
};

export const getNameFromPointSpreadValue = (name: string): string => {
	if (typeof name !== 'string') {
		return '';
	}

	return name.replace(/[+-]?(?:\d*\.)?\d+$/, '').trim();
};

export const isStrictlyUSHandicap = (bet): boolean => {
	if (!bet) {
		return false;
	}
	return (
		bet.marketName === 'Point Spread' ||
		bet.marketName === 'Moneyline' ||
		bet.marketName === 'Runline'
	);
};

// This is faster that Lodash's cloneDeep and preferred as sometimes we need to copy the full Baobab tree
// https://stackoverflow.com/a/44612374
export function cloneDeep(o) {
	let newO;
	let i;

	if (typeof o !== 'object') {
		return o;
	}

	if (!o) {
		return o;
	}

	if (Object.prototype.toString.apply(o) === '[object Array]') {
		newO = [];
		for (i = 0; i < o.length; i += 1) {
			newO[i] = cloneDeep(o[i]);
		}
		return newO;
	}

	newO = {};
	for (i in o) {
		// eslint-disable-next-line no-prototype-builtins
		if (o.hasOwnProperty(i)) {
			newO[i] = cloneDeep(o[i]);
		}
	}
	return newO;
}

export const getUserGeoStateLocation = (geoStateLocation: string) => {
	return geoStateLocation in US_STATE_CODE ? geoStateLocation : 'NJ';
};

export const getSearchString = (url = ''): string => {
	return url.split('?')[1];
};

export const getSearchParams = (
	url: string,
): [string, string][] | undefined => {
	if (!url) return;

	try {
		const searchString: string = getSearchString(url);
		const searchParams: URLSearchParams = new URLSearchParams(searchString);

		return [...searchParams.entries()];
	} catch (err) {
		console.log('error at hasSearchParams');
		console.error(err);
	}
};

export const getSearchParamsAndHashFragment = (url: string) => {
	const [currentPath, queryParams = '']: string[] = url.split('?');
	const [searchString = '', hashFragment = '']: string[] =
		queryParams.split('#');

	const searchParams: URLSearchParams = new URLSearchParams(searchString);

	const hasSearchParams = !![...searchParams.entries()].length;
	const hasHashFragment = !!hashFragment;

	const searchParamsString: string = hasSearchParams
		? `?${searchParams.toString()}`
		: '';
	const hashFragmentString: string = hasHashFragment ? `#${hashFragment}` : '';

	return {
		currentPath,
		hasSearchParams,
		searchParams,
		searchParamsString,
		hasHashFragment,
		hashFragment,
		hashFragmentString,
	};
};

export function getQueryParam(param: string): Array<any> {
	if (!param || param.length === 0) {
		return [];
	}

	const query = window.location.search.toLowerCase();
	let values = query.substring(query.indexOf(param) + param.length + 1);
	if (values.indexOf('&') != -1) {
		values = values.substring(0, values.indexOf('&'));
	}

	// if no betIds we return an empty array so we can go into contextualMode.
	if (values.length === 0) {
		return [];
	}

	return values.split(',');
}

export const gtmTrackingLogInClicked = (): void => {
	const trackingActions = new TrackingActions();
	trackingActions.eventTracking({
		event: TRACKING_EVENTS.LOGIN_NAVIGATION,
		eventCategory: TRACKING_CATEGORIES.ODDSCHECKER_ACCOUNT,
		eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.LOGIN_NAVIGATION],
		eventLabel: 'Log in link',
	});
};

export const articleTypeHelper = (
	isPremium: boolean,
	premiumDisplay: string,
) => {
	const showPaywall =
		premiumDisplay === 'changePlan' || premiumDisplay === 'premiumUpgrade';

	if (isPremium) {
		if (showPaywall) return 'Blocked Content';
		return 'Premium';
	}
	return 'Free';
};

export const concatenateArticleAuthors = (
	authors: IArticleAuthor[],
): string => {
	if (!authors?.length) return '';

	return authors.map(({ fullName }) => fullName).join('&');
};
